import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, TrendingUp, FileCheck, Users, CheckCircle, ArrowRight, Moon, Sun } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fundingbridge/artifacts/6f4haw8y_9161E8E4-2A18-454E-B39C-D1A5E84AD155.png';

const Home = () => {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300" data-testid="home-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Theme Toggle */}
        <div className="absolute top-6 right-6 z-10">
          <Button
            onClick={toggleTheme}
            variant="outline"
            size="sm"
            className="glass-effect border-slate-300 dark:border-slate-700 hover-scale"
          >
            {isDark ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-slate-700" />}
          </Button>
        </div>

        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 dark:from-blue-600/20 dark:to-purple-600/20 rounded-full blur-3xl animate-float"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 dark:from-cyan-600/20 dark:to-blue-600/20 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            {/* Logo */}
            <div className="mb-6 animate-scale-in">
              <img 
                src={LOGO_URL} 
                alt="Our Cap Logo" 
                className="h-40 w-auto mx-auto object-contain"
              />
            </div>
            
            <h1 className="text-6xl font-bold text-slate-900 dark:text-slate-100 mb-6 leading-tight animate-slide-in-up">
              Welcome to <span className="text-gradient">Our Cap</span>
            </h1>
            
            <h2 className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-6 animate-slide-in-up" style={{animationDelay: '0.1s'}}>
              Simple Capital. Precise Execution.
            </h2>
            
            <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto mb-8 leading-relaxed animate-slide-in-up" style={{animationDelay: '0.2s'}}>
              Our Cap coordinates verified liquidity support for real estate transactions requiring proof of reserves. We manage entity formation, documentation, and execution so borrowers can meet lender requirements and close with confidence.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 animate-slide-in-up" style={{animationDelay: '0.2s'}}>
              <Link to="/register">
                <Button size="lg" className="text-lg px-8 py-6 gradient-primary border-0 hover:shadow-xl hover:shadow-blue-500/40 transition-all hover-scale" data-testid="get-started-button">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all hover-scale" data-testid="sign-in-button">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Image Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img 
              src="https://images.unsplash.com/photo-1709080381729-965c62ab0471?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxyZWFsJTIwZXN0YXRlJTIwaW52ZXN0bWVudCUyMHByb3BlcnR5fGVufDB8fHx8MTc2ODkyMjM4NXww&ixlib=rb-4.1.0&q=85" 
              alt="Real Estate Investment" 
              className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
              <p className="text-white font-semibold">Real Estate Investments</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img 
              src="https://images.unsplash.com/photo-1759310610325-2c7cb621e5e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwJTIwZGVhbHxlbnwwfHx8fDE3Njg5MjIzODZ8MA&ixlib=rb-4.1.0&q=85" 
              alt="Business Partnership" 
              className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
              <p className="text-white font-semibold">Trusted Partnerships</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img 
              src="https://images.unsplash.com/photo-1768207450151-30c0bf8e8091?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxmaW5hbmNlJTIwbW9uZXklMjBpbnZlc3RtZW50JTIwZ3Jvd3RofGVufDB8fHx8MTc2ODkyMjM4OXww&ixlib=rb-4.1.0&q=85" 
              alt="Financial Growth" 
              className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
              <p className="text-white font-semibold">Financial Growth</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12 animate-slide-in-up">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-4">Why Choose Our Cap?</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">Secure, verified, and efficient liquidity coordination</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-slate-200 dark:border-slate-700 hover:shadow-2xl transition-all duration-300 group glass-effect hover-lift animate-scale-in">
            <CardHeader>
              <div className="w-14 h-14 gradient-primary rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-blue-500/20">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-slate-900 dark:text-slate-100">Secure Platform</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 dark:text-slate-400">Complete privacy controls ensure borrower and LSP identities remain protected throughout the process.</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 dark:border-slate-700 hover:shadow-2xl transition-all duration-300 group glass-effect hover-lift animate-scale-in" style={{animationDelay: '0.1s'}}>
            <CardHeader>
              <div className="w-14 h-14 gradient-success rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-green-500/20">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-slate-900 dark:text-slate-100">Verified Liquidity</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 dark:text-slate-400">Bank verification through Plaid ensures LSP liquidity is legitimate and available when needed.</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 dark:border-slate-700 hover:shadow-2xl transition-all duration-300 group glass-effect hover-lift animate-scale-in" style={{animationDelay: '0.2s'}}>
            <CardHeader>
              <div className="w-14 h-14 gradient-accent rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                <FileCheck className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-slate-900 dark:text-slate-100">Document Management</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 dark:text-slate-400">Complete document workflow with e-signature capabilities for all agreements and compliance.</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 dark:border-slate-700 hover:shadow-2xl transition-all duration-300 group glass-effect hover-lift animate-scale-in" style={{animationDelay: '0.3s'}}>
            <CardHeader>
              <div className="w-14 h-14 gradient-secondary rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-cyan-500/20">
                <Users className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-slate-900 dark:text-slate-100">Admin Control</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 dark:text-slate-400">Full administrative oversight with audit logs and approval workflows for transparency.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-4">How It Works</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">Simple, transparent process from start to finish</p>
        </div>

        <div className="space-y-6">
          {[
            { step: '1', title: 'Borrower Submits Deal', desc: 'Complete deal intake form with property details and lender requirements' },
            { step: '2', title: 'Admin Review', desc: 'Our team reviews and approves qualified deals for marketplace listing' },
            { step: '3', title: 'LSP Commitment', desc: 'Verified LSPs view anonymized deals and commit liquidity support' },
            { step: '4', title: 'Match Approval', desc: 'Admin approves the match and coordinates documentation' },
            { step: '5', title: 'Closing', desc: 'Complete transaction with all parties coordinated through the platform' }
          ].map((item) => (
            <Card key={item.step} className="border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all glass-effect">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <span className="text-white font-bold text-lg">{item.step}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">{item.title}</h3>
                    <p className="text-slate-600 dark:text-slate-400">{item.desc}</p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-gradient-to-r from-slate-100 to-blue-50 dark:from-slate-800 dark:to-slate-900 border-t border-slate-200 dark:border-slate-700 py-12 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-effect rounded-2xl p-8 border border-slate-200 dark:border-slate-700">
            <p className="text-center text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
              <strong className="text-slate-900 dark:text-slate-100">Important Disclosure:</strong> Our Cap is a coordination platform. We are not a lender and do not provide loan funds.
              There is no guarantee of loan approval or closing. All users must submit accurate and truthful information.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <img 
              src={LOGO_URL} 
              alt="Our Cap Logo" 
              className="h-20 w-auto mx-auto mb-4 object-contain"
            />
            <div className="flex justify-center gap-6 mb-4">
              <Link to="/terms" className="text-sm text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                Privacy Policy
              </Link>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">&copy; {new Date().getFullYear()} Our Cap. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;